import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, FormArray, Validators } from '@angular/forms';
import {
  professionalServiceOptions,
  pentestTypeOptions,
  assessmentPackageOptions,
} from '../../../mock-data/services-mock-data';
import { ProfessionalServicesData } from '../../../../../../../../../core/models/customer';

export interface ProfessionalServicesForm {
  selectedServices: FormControl<string[] | null>;
  pentestTypes: FormControl<string[] | null>;
  subnetCount: FormControl<number | null>;
  domains: FormControl<string | null>;
  pentestStartDate: FormControl<Date | null>;
  ipSubnets: FormControl<number | null>;
  asaStartDate: FormControl<Date | null>;
  assessmentPackage: FormControl<any | null>;
  baaStartDate: FormControl<Date | null>;
}

@Injectable({
  providedIn: 'root',
})
export class ProfessionalServicesFormService {
  constructor(private fb: FormBuilder) {}

  createProfessionalServicesForm(data?: ProfessionalServicesData): FormGroup<ProfessionalServicesForm> {
    const selectedServices = data?.selectedServices || [];

    const form = this.fb.group<ProfessionalServicesForm>({
      selectedServices: this.fb.control<string[] | null>(selectedServices, [
        Validators.required,
        Validators.minLength(1),
      ]),
      pentestTypes: this.fb.control<string[] | null>(data?.penetrationTesting?.types || []),
      subnetCount: this.fb.control<number | null>(data?.penetrationTesting?.subnetCount || null),
      domains: this.fb.control<string | null>(data?.penetrationTesting?.domains || ''),
      pentestStartDate: this.fb.control<Date | null>(data?.penetrationTesting?.startDate || null),
      ipSubnets: this.fb.control<number | null>(data?.attackSurfaceAssessment?.ipSubnets || null),
      asaStartDate: this.fb.control<Date | null>(data?.attackSurfaceAssessment?.startDate || null),
      assessmentPackage: this.fb.control<any | null>(data?.breachAttackAssessment?.package || null),
      baaStartDate: this.fb.control<Date | null>(data?.breachAttackAssessment?.startDate || null),
    });

    if (data) {
      this.patchFormWithData(form, data);
    }

    // Apply validators for any services that are already selected
    if (selectedServices.length > 0) {
      this.applyValidationForSelectedServices(form, selectedServices);
    }

    return form;
  }

  private patchFormWithData(form: FormGroup<ProfessionalServicesForm>, data: ProfessionalServicesData): void {
    const selectedServices = data.selectedServices || [];

    form.patchValue({
      selectedServices: selectedServices,
      pentestTypes: data.penetrationTesting?.types || [],
      subnetCount: data.penetrationTesting?.subnetCount || null,
      domains: data.penetrationTesting?.domains || '',
      pentestStartDate: data.penetrationTesting?.startDate || null,
      ipSubnets: data.attackSurfaceAssessment?.ipSubnets || null,
      asaStartDate: data.attackSurfaceAssessment?.startDate || null,
      assessmentPackage: data.breachAttackAssessment?.package || null,
      baaStartDate: data.breachAttackAssessment?.startDate || null,
    });
  }

  toggleService(form: FormGroup<ProfessionalServicesForm>, serviceValue: string): void {
    const currentServices = form.get('selectedServices')?.value || [];

    let updatedServices: string[];

    if (currentServices.includes(serviceValue)) {
      // Remove service
      updatedServices = currentServices.filter((service: string) => service !== serviceValue);
      this.clearServiceData(form, serviceValue);
    } else {
      // Add service
      updatedServices = [...currentServices, serviceValue];
    }

    form.patchValue({ selectedServices: updatedServices });

    // Apply validation based on all selected services
    this.applyValidationForSelectedServices(form, updatedServices);
  }

  // Method to handle conditional validation when testing types change
  updatePentestValidation(form: FormGroup<ProfessionalServicesForm>): void {
    const pentestTypes = form.get('pentestTypes')?.value || [];
    const domainsControl = form.get('domains');

    if (pentestTypes.includes('web-application')) {
      // Web application is selected, domains field should be required
      domainsControl?.setValidators([Validators.required]);
    } else {
      // Web application is not selected, domains field is optional
      domainsControl?.clearValidators();
    }

    // Update validity without triggering valueChanges
    domainsControl?.updateValueAndValidity({ emitEvent: false });
  }

  // Comprehensive validation method that applies validators based on selected services
  private applyValidationForSelectedServices(
    form: FormGroup<ProfessionalServicesForm>,
    selectedServices: string[]
  ): void {
    // First, clear all validators
    this.clearAllValidators(form);

    // Then apply validators based on selected services
    selectedServices.forEach((service) => {
      switch (service) {
        case 'penetration-testing':
          form.get('pentestTypes')?.setValidators([Validators.required]);
          form.get('subnetCount')?.setValidators([Validators.required, Validators.min(1)]);
          form.get('pentestStartDate')?.setValidators([Validators.required]);
          break;
        case 'attack-surface-assessment':
          form.get('ipSubnets')?.setValidators([Validators.required, Validators.min(1)]);
          form.get('asaStartDate')?.setValidators([Validators.required]);
          break;
        case 'breach-attack-assessment':
          form.get('assessmentPackage')?.setValidators([Validators.required]);
          form.get('baaStartDate')?.setValidators([Validators.required]);
          break;
      }
    });

    // Update all form controls without triggering valueChanges
    this.updateAllFormControls(form);
  }

  // Clear all validators from form controls
  private clearAllValidators(form: FormGroup<ProfessionalServicesForm>): void {
    form.get('pentestTypes')?.clearValidators();
    form.get('subnetCount')?.clearValidators();
    form.get('pentestStartDate')?.clearValidators();
    form.get('domains')?.clearValidators();
    form.get('ipSubnets')?.clearValidators();
    form.get('asaStartDate')?.clearValidators();
    form.get('assessmentPackage')?.clearValidators();
    form.get('baaStartDate')?.clearValidators();
  }

  // Update all form controls without triggering valueChanges
  private updateAllFormControls(form: FormGroup<ProfessionalServicesForm>): void {
    form.get('pentestTypes')?.updateValueAndValidity({ emitEvent: false });
    form.get('subnetCount')?.updateValueAndValidity({ emitEvent: false });
    form.get('pentestStartDate')?.updateValueAndValidity({ emitEvent: false });
    form.get('domains')?.updateValueAndValidity({ emitEvent: false });
    form.get('ipSubnets')?.updateValueAndValidity({ emitEvent: false });
    form.get('asaStartDate')?.updateValueAndValidity({ emitEvent: false });
    form.get('assessmentPackage')?.updateValueAndValidity({ emitEvent: false });
    form.get('baaStartDate')?.updateValueAndValidity({ emitEvent: false });
  }

  // Public method to reapply validation for all selected services
  public reapplyValidationForSelectedServices(form: FormGroup<ProfessionalServicesForm>): void {
    const selectedServices = form.get('selectedServices')?.value || [];
    this.applyValidationForSelectedServices(form, selectedServices);
  }

  private clearServiceData(form: FormGroup<ProfessionalServicesForm>, serviceValue: string): void {
    switch (serviceValue) {
      case 'penetration-testing':
        form.patchValue({
          pentestTypes: [],
          subnetCount: null,
          domains: '',
          pentestStartDate: null,
        });
        break;
      case 'attack-surface-assessment':
        form.patchValue({
          ipSubnets: null,
          asaStartDate: null,
        });
        break;
      case 'breach-attack-assessment':
        form.patchValue({
          assessmentPackage: null,
          baaStartDate: null,
        });
        break;
    }
  }

  getFormData(form: FormGroup<ProfessionalServicesForm>): ProfessionalServicesData {
    const value = form.value;
    const selectedServices = value.selectedServices || [];

    const result: ProfessionalServicesData = {
      selectedServices,
    };

    // Add penetration testing data if selected
    if (selectedServices.includes('penetration-testing')) {
      result.penetrationTesting = {
        types: value.pentestTypes || [],
        subnetCount: value.subnetCount || 0,
        domains: value.domains || '',
        startDate: value.pentestStartDate || new Date(),
      };
    }

    // Add attack surface assessment data if selected
    if (selectedServices.includes('attack-surface-assessment')) {
      result.attackSurfaceAssessment = {
        ipSubnets: value.ipSubnets || 0,
        startDate: value.asaStartDate || new Date(),
      };
    }

    // Add breach attack assessment data if selected
    if (selectedServices.includes('breach-attack-assessment')) {
      result.breachAttackAssessment = {
        package: this.getDropdownValue(value.assessmentPackage) || '',
        startDate: value.baaStartDate || new Date(),
      };
    }

    return result;
  }

  getDropdownValue(value: any): string {
    return value?.value || value || '';
  }

  findDropdownObject(options: any[], value: string): any {
    return options.find((option) => option.value === value);
  }

  getServiceOptions() {
    return professionalServiceOptions;
  }

  getPentestTypeOptions() {
    return pentestTypeOptions;
  }

  getAssessmentPackageOptions() {
    return assessmentPackageOptions;
  }

  debugFormState(form: FormGroup<ProfessionalServicesForm>): void {
    console.log('=== Professional Services Form Debug ===');
    console.log('Form Valid:', form.valid);
    console.log('Form Invalid:', form.invalid);
    console.log('Form Pending:', form.pending);
    console.log('Form Dirty:', form.dirty);
    console.log('Form Touched:', form.touched);
    console.log('Form Errors:', form.errors);
    console.log('Selected Services:', form.get('selectedServices')?.value);
    console.log('Selected Services Valid:', form.get('selectedServices')?.valid);
    console.log('Selected Services Errors:', form.get('selectedServices')?.errors);

    // Log all form controls
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      console.log(`${key}:`, {
        value: control?.value,
        valid: control?.valid,
        invalid: control?.invalid,
        errors: control?.errors,
        touched: control?.touched,
        dirty: control?.dirty,
      });
    });
  }
}
